import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Share2, Play, MessageCircle, ChevronDown, ChevronUp, Send, Bookmark, Heart, Flag, Trash2, Pencil } from 'lucide-react';
import { useVideos } from '../../hooks/useVideos';
import { useVideoEngagement } from '../../hooks/useVideoEngagement';
import { youtubeEmbedSrc } from '../../lib/youtubeEmbed';
import { mountVideo, preloadHls } from '../../lib/hlsPlayer';
import { useVerification } from '../../hooks/useVerification';
import { useUser } from '../../contexts/UserContext';
import VerifyModal from '../common/VerifyModal';
import ReportModal from '../common/ReportModal';
import SoonTag from '../common/SoonTag';
import EditedMark from '../common/EditedMark';
import LinkifiedText from '../common/LinkifiedText';
import Toast from '../common/Toast';
import useToast from '../../hooks/useToast';
import { useVideoComments, commentTime, commentAuthorName, MAX_COMMENT_LENGTH } from '../../hooks/useVideoComments';
import { getRoleLabel } from '../../lib/roleLabels';
import TeamTag from '../common/TeamTag';
import { shareLink, shareToastMessage } from '../../lib/share';
import { supabase } from '../../lib/supabase';
import RemoveVideoDialog from '../common/RemoveVideoDialog';


const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

const VideoScreen = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { verified, showVerifyModal, setShowVerifyModal, requireVerified } = useVerification();
  const { session, userData } = useUser();
  const { videos, loading: videosLoading } = useVideos();
  const video = videos.find(v => String(v.id) === videoId);
  const { engagement, toggleLike: toggleVideoLike, logPlay } = useVideoEngagement(videoId ? [videoId] : []);
  const videoEngagement = engagement[String(videoId)] || { likeCount: 0, liked: false };

  const playerRef = useRef(null);
  const cleanupRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [reportComment, setReportComment] = useState(null);   // comment id being reported
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingComment, setEditingComment] = useState(null);  // comment id
  const [commentDraft, setCommentDraft] = useState('');
  const [commentEditError, setCommentEditError] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const { toastMsg, showToast } = useToast();
  const {
    comments,
    loading: commentsLoading,
    error: commentError,
    setError: setCommentError,
    addComment,
    removeComment,
    editComment,
  } = useVideoComments(videoId);

  // ── Moderator takedown (045) ───────────────────────────────────────
  // Here rather than in /admin because this is where a moderator IS when they
  // decide: watching the video. The review queue only ever holds PENDING rows,
  // so before 045 an approved video could only be removed if somebody reported
  // it first.
  const [confirmRemove, setConfirmRemove] = useState(false);

  const canModerate = ['moderator', 'admin', 'superadmin'].includes(userData?.app_role);
  // Only community submissions are removable this way. Curated videos have
  // their own hide/delete at /admin/curated-videos, and mock videos are code.
  const isRemovable = Boolean(video?.isSubmission);

  // Resolves with a message to display, or null on success.
  const handleRemoveVideo = async (reason) => {
    const { error } = await supabase.rpc('admin_remove_video', {
      p_submission_id: video.id,
      p_reason: reason,
    });
    if (error) {
      return error.message?.includes('unauthorized')
        ? 'You do not have permission to remove this video.'
        : 'Could not remove this video. Please try again.';
    }
    // useVideos fetches on mount, so returning to the library re-fetches
    // without the removed video rather than leaving a stale card behind.
    navigate('/explore/videos', { replace: true });
    return null;
  };

  // Warm the hls.js chunk so the dynamic import is already resolved by the time
  // someone taps play. No-op on platforms that play HLS natively.
  useEffect(() => { preloadHls(); }, []);

  // hls.js holds XHRs, timers and a worker — unmounting without destroying it
  // leaks all three. The old innerHTML player needed no teardown, so this is
  // genuinely new bookkeeping rather than a pre-existing omission.
  useEffect(() => () => { cleanupRef.current?.(); }, []);

  if (videosLoading && !video) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(165deg, var(--dark-bg-start) 0%, var(--dark-bg-mid) 40%, var(--dark-bg-end) 100%)` }}>
        <span className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'rgba(255,255,255,0.2)', borderTopColor: '#fff' }} />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(165deg, var(--dark-bg-start) 0%, var(--dark-bg-mid) 40%, var(--dark-bg-end) 100%)` }}>
        <div className="text-center">
          <p className="text-primary text-lg font-semibold mb-2">Video not found</p>
          <button onClick={() => navigate('/feed')} className="text-brand-accent text-sm">Back to Feed</button>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const msg = shareToastMessage(await shareLink({
      title: video.title,
      path: `/video/${video.id}`,
    }));
    if (msg) showToast(msg);
  };

  const handlePostComment = async () => {
    if (!requireVerified()) return;
    if (!draft.trim() || posting) return;
    setPosting(true);
    const ok = await addComment(draft);
    setPosting(false);
    if (ok) setDraft('');
  };

  return (
    <div
      className="min-h-screen pb-nav"
      style={{
        background: `linear-gradient(165deg, var(--dark-bg-start) 0%, var(--dark-bg-mid) 40%, var(--dark-bg-end) 100%)`,
      paddingTop: 'var(--sat)',
      }}
    >
      {/* Desktop: two-column grid │ Mobile: single column */}
      <div className="md:px-6 md:pt-5 md:pb-8 md:grid md:grid-cols-[1fr_360px] md:gap-6 md:items-start">

        {/* ═══ LEFT: video + details ═══ */}
        <div className="min-w-0">

        {/* Video Player */}
        <div className="relative aspect-video bg-black overflow-hidden md:rounded-2xl group">
          {/* Thumbnail — always rendered as base layer */}
          <img
            src={video.thumbnail}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
            style={{ opacity: playing ? 0 : 1 }}
          />

          {/* Play button overlay */}
          {!playing && (
            <button
              onClick={() => {
                setPlaying(true);
                logPlay(video.id);
                if (!playerRef.current) return;

                // Everything below builds DOM with createElement rather than
                // innerHTML. The old string form interpolated video.title and
                // video.captionUrl unescaped, which was a stored-XSS sink the
                // moment anything but an admin could set those.
                cleanupRef.current?.();
                cleanupRef.current = null;
                const host = playerRef.current;
                host.textContent = '';

                const playbackSrc = video.streamHlsUrl || video.storageUrl;

                if (video.youtubeId) {
                  const iframe = document.createElement('iframe');
                  iframe.src = youtubeEmbedSrc(video.youtubeId);
                  iframe.title = video.title || 'Video';
                  iframe.className = 'absolute inset-0 w-full h-full';
                  iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
                  iframe.allowFullscreen = true;
                  iframe.style.border = 'none';
                  host.appendChild(iframe);
                  cleanupRef.current = () => { host.textContent = ''; };
                } else if (playbackSrc) {
                  // mountVideo handles both HLS and plain MP4, so the dual-read
                  // fallback to storage_url needs no extra branch here.
                  cleanupRef.current = mountVideo(host, {
                    src: playbackSrc,
                    poster: video.thumbnail,
                    captionUrl: video.hasCaptions ? video.captionUrl : null,
                  });
                } else {
                  const wrap = document.createElement('div');
                  wrap.className = 'absolute inset-0 flex items-center justify-center';
                  wrap.style.background = 'rgba(0,0,0,0.85)';
                  const inner = document.createElement('div');
                  inner.className = 'text-center px-6';
                  const h = document.createElement('p');
                  h.className = 'text-white font-semibold mb-1';
                  h.textContent = 'Full video coming soon';
                  const sub = document.createElement('p');
                  sub.className = 'text-sm';
                  sub.style.color = 'rgba(255,255,255,0.5)';
                  sub.textContent = 'Available in the DSP Cafe app';
                  inner.append(h, sub);
                  wrap.appendChild(inner);
                  host.appendChild(wrap);
                  cleanupRef.current = () => { host.textContent = ''; };
                }
              }}
              className="absolute inset-0 flex items-center justify-center z-10 group"
            >
              <div className="absolute inset-0 bg-black/20" />
              <div
                className="relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-active:scale-95"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
              >
                <Play size={28} fill="var(--brand-primary)" style={{ color: 'var(--brand-primary)', marginLeft: 3 }} />
              </div>
            </button>
          )}

          {/* Player container — iframe injected here synchronously on tap to preserve iOS gesture context */}
          {/* pointer-events toggled so empty div doesn't block play button, but active iframe is interactive */}
          <div ref={playerRef} className="absolute inset-0 z-10" style={{ pointerEvents: playing ? 'auto' : 'none' }} />

          {/* Gradient scrim + safe-area-aware back button */}
          <div
            className={`absolute top-0 left-0 right-0 z-20 pointer-events-none transition-opacity duration-300 ${playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)',
              height: '5.5rem',
            }}
          >
            <div
              className="px-4 flex items-center"
              style={{ paddingTop: '0.75rem' }}
            >
              <button
                onClick={() => navigate(-1)}
                className="pointer-events-auto w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: 'rgba(0,0,0,0.35)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <ArrowLeft className="text-white" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Video Details */}
        <div className="p-4 md:px-0 md:pt-4 space-y-4">
          <div>
            {video.postTitle ? (
              <>
                <h1 className="text-xl font-bold mb-1 text-primary">{video.postTitle}</h1>
                <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>
                  YouTube: {video.title}
                </p>
              </>
            ) : (
              <h1 className="text-xl font-bold mb-2 text-primary">{video.title}</h1>
            )}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))' }}
                >
                  {video.creator?.[0] ?? '?'}
                </div>
                <div>
                  {video.submitterId ? (
                    <button
                      onClick={() => navigate(`/profile/${video.submitterId}`)}
                      className="text-sm font-semibold text-primary text-left hover:underline transition-colors"
                    >
                      {video.creator}
                    </button>
                  ) : (
                    <p className="text-sm font-semibold text-primary">{video.creator}</p>
                  )}
                  {video.views != null && (
                    <p className="text-xs text-tertiary">{video.views} views</p>
                  )}
                </div>
              </div>
              {video.category && (
                <span
                  className="px-3 py-1 rounded-full text-xs font-medium text-brand-accent flex-shrink-0"
                  style={{ backgroundColor: `rgba(var(--brand-accent-rgb), 0.15)` }}
                >
                  {video.category}
                </span>
              )}
            </div>
          </div>

          {video.commentary && (
            <p className="text-sm leading-relaxed -mt-1" style={{ color: 'var(--text-secondary)' }}>
              {video.commentary}
            </p>
          )}

          {/* The conversation this video was suggested from (039). */}
          {video.sourcePost && (
            <Link
              to={`/community/${video.sourcePost.tableSlug}/${video.sourcePost.id}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium -mt-1 hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              <MessageCircle size={15} />
              {video.sourcePost.tableName
                ? `Shared in a discussion in ${video.sourcePost.tableName}`
                : 'Shared in a table discussion'}
            </Link>
          )}

          <div className="flex items-center justify-around" style={{ borderTop: '1px solid var(--card-border)', borderBottom: '1px solid var(--card-border)' }}>
            {[
              {
                icon: <Heart size={20} className={videoEngagement.liked ? 'fill-current' : ''} />,
                label: videoEngagement.likeCount > 0 ? `${videoEngagement.likeCount}` : 'Like',
                action: () => toggleVideoLike(video.id),
                active: videoEngagement.liked,
              },
              // Save has no backend yet (needs a saved_items table); Share
              // needs none, so it is real.
              { icon: <Bookmark size={20} />, label: 'Save', soon: true },
              { icon: <Share2 size={20} />, label: 'Share', action: handleShare },
              // Report only applies to community submissions (not mock/curated)
              // and not the submitter's own video
              ...(video.submitterId && video.submitterId !== session?.user?.id
                ? [{ icon: <Flag size={20} />, label: 'Report', action: () => setShowReport(true) }]
                : []),
              // 045: moderator takedown of an already-approved submission.
              ...(canModerate && isRemovable
                ? [{ icon: <Trash2 size={20} />, label: 'Remove', action: () => setConfirmRemove(true) }]
                : []),
            ].map(({ icon, label, action, active, soon }) => (
              <button
                key={label}
                onClick={action}
                disabled={soon}
                aria-disabled={soon}
                aria-label={soon ? `${label} — coming soon` : label}
                className={`flex flex-col items-center gap-1 px-6 py-2 rounded-xl transition-all duration-200 ${soon ? 'cursor-default' : 'hover:scale-105 active:scale-95'} ${active ? 'text-rose-500' : ''}`}
                style={active ? undefined : { color: soon ? 'var(--text-tertiary)' : 'var(--text-secondary)' }}
              >
                {icon}
                {soon ? <SoonTag /> : <span className="text-xs font-medium">{label}</span>}
              </button>
            ))}
          </div>

          {/* Comments Section */}
          <div
            className="rounded-xl overflow-hidden"
            style={cardStyle}
          >
            {/* Comments header / toggle */}
            <button
              onClick={() => setCommentsOpen(!commentsOpen)}
              className="w-full px-4 py-3 flex items-center justify-between transition-all hover-surface"
            >
              <div className="flex items-center gap-2">
                <MessageCircle size={18} style={{ color: 'var(--text-tertiary)' }} />
                <span className="font-semibold text-sm text-primary">
                  {commentsLoading ? 'Comments' : `Comments (${comments.length})`}
                </span>
              </div>
              {commentsOpen
                ? <ChevronUp size={18} style={{ color: 'var(--text-tertiary)' }} />
                : <ChevronDown size={18} style={{ color: 'var(--text-tertiary)' }} />
              }
            </button>

            {/* Preview when collapsed */}
            {!commentsOpen && comments.length > 0 && (
              <div className="px-4 pb-3 flex items-center gap-2.5">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))' }}
                >
                  {comments[0].author_avatar
                    ? <img src={comments[0].author_avatar} alt="" className="w-full h-full object-cover" />
                    : commentAuthorName(comments[0])[0]?.toUpperCase()}
                </div>
                <p className="text-xs text-tertiary truncate">
                  <span className="text-secondary font-medium">{commentAuthorName(comments[0])}</span>{' '}
                  {comments[0].content}
                </p>
              </div>
            )}

            {/* Expanded comments */}
            {commentsOpen && (
              <div>
                {/* Comment input */}
                <div
                  className="px-4 pb-3"
                  style={{ borderBottom: '1px solid var(--card-border)' }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))' }}
                    >
                      {userData?.avatar_url
                        ? <img src={userData.avatar_url} alt="" className="w-full h-full object-cover" />
                        : (userData?.name?.[0]?.toUpperCase() || 'D')}
                    </div>
                    <div
                      className="flex-1 flex items-center rounded-lg overflow-hidden"
                      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
                    >
                      <input
                        type="text"
                        value={draft}
                        maxLength={MAX_COMMENT_LENGTH}
                        onChange={(e) => { setDraft(e.target.value); if (commentError) setCommentError(''); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handlePostComment(); }}
                        placeholder={verified ? 'Add a comment...' : 'Verify to comment...'}
                        className="flex-1 px-3 py-2 text-sm text-primary bg-transparent focus:outline-none"
                        onFocus={(e) => { if (!verified) { e.target.blur(); requireVerified(); } }}
                      />
                      <button
                        onClick={handlePostComment}
                        disabled={posting || !draft.trim()}
                        className="px-3 py-2 transition-all hover:scale-110 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
                        style={{ color: 'var(--brand-primary)' }}
                        aria-label="Post comment"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                  {commentError && (
                    <p className="text-xs mt-2 ml-11" style={{ color: '#C0392B' }}>{commentError}</p>
                  )}
                </div>

                {/* Comment list */}
                <div className="px-4 py-3 space-y-4">
                  {commentsLoading && (
                    <p className="text-xs text-tertiary py-2">Loading comments…</p>
                  )}

                  {!commentsLoading && comments.length === 0 && (
                    <p className="text-xs text-tertiary py-2">
                      No comments yet — be the first to share what you took from this.
                    </p>
                  )}

                  {!commentsLoading && comments.map(comment => {
                    const isMine = comment.author_id === session?.user?.id;
                    const name = commentAuthorName(comment);
                    return (
                      <div key={comment.id} className="flex gap-3 group">
                        <button
                          onClick={() => !isMine && navigate(`/profile/${comment.author_id}`)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5 overflow-hidden"
                          style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))' }}
                        >
                          {comment.author_avatar
                            ? <img src={comment.author_avatar} alt="" className="w-full h-full object-cover" />
                            : name[0]?.toUpperCase()}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-primary">{name}</span>
                            <TeamTag userId={comment.author_id} className="self-center" />
                            {/* getRoleLabel, not the raw column: 'student' is a
                                DB id, 'New DSP' is the label every
                                other byline in the app shows. */}
                            {getRoleLabel(comment.author_role) && (
                              <span className="text-xs text-tertiary">{getRoleLabel(comment.author_role)}</span>
                            )}
                            <span className="text-xs text-tertiary">{commentTime(comment.created_at)}</span>
                            <EditedMark at={comment.edited_at} className="text-xs" />
                          </div>
                          {editingComment === comment.id ? (
                            <div className="mt-1">
                              <textarea
                                value={commentDraft}
                                onChange={(e) => setCommentDraft(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
                                rows={3}
                                autoFocus
                                className="w-full px-3 py-2 rounded-lg text-[15px] text-secondary resize-y focus:outline-none focus:ring-1 focus:ring-brand-secondary"
                                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
                              />
                              {commentEditError && (
                                <p className="text-xs mt-1" style={{ color: 'var(--color-error)' }}>{commentEditError}</p>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-[11px] text-tertiary">{commentDraft.length}/{MAX_COMMENT_LENGTH}</span>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setEditingComment(null)}
                                    className="px-3 py-1.5 rounded-lg text-xs transition-all active:scale-[0.98]"
                                    style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)' }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={async () => {
                                      const next = commentDraft.trim();
                                      if (!next || commentSaving) return;
                                      if (next === comment.content) { setEditingComment(null); return; }
                                      setCommentSaving(true);
                                      const msg = await editComment(comment.id, next);
                                      setCommentSaving(false);
                                      if (msg) { setCommentEditError(msg); return; }
                                      setEditingComment(null);
                                    }}
                                    disabled={commentSaving || !commentDraft.trim()}
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                                    style={{ backgroundColor: 'var(--brand-primary)' }}
                                  >
                                    {commentSaving ? 'Saving…' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <LinkifiedText
                              text={comment.content}
                              className="text-[15px] text-secondary mt-0.5 leading-relaxed"
                            />
                          )}

                          {/* Own comment: delete. Someone else's: report.
                              Never both, and never report your own. */}
                          <div className="flex items-center gap-3 mt-1.5">
                            {isMine ? (
                              confirmDelete === comment.id ? (
                                <>
                                  <button
                                    onClick={async () => { setConfirmDelete(null); await removeComment(comment.id); }}
                                    className="text-xs font-semibold"
                                    style={{ color: '#C0392B' }}
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => setConfirmDelete(null)}
                                    className="text-xs text-tertiary"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => {
                                      setCommentDraft(comment.content);
                                      setCommentEditError('');
                                      setEditingComment(comment.id);
                                    }}
                                    className="flex items-center gap-1 transition-opacity opacity-60 hover:opacity-100"
                                    style={{ color: 'var(--text-tertiary)' }}
                                    aria-label="Edit comment"
                                  >
                                    <Pencil size={13} />
                                    <span className="text-xs">Edit</span>
                                  </button>
                                  <button
                                    onClick={() => setConfirmDelete(comment.id)}
                                    className="flex items-center gap-1 transition-opacity opacity-60 hover:opacity-100"
                                    style={{ color: 'var(--text-tertiary)' }}
                                    aria-label="Delete comment"
                                  >
                                    <Trash2 size={13} />
                                    <span className="text-xs">Delete</span>
                                  </button>
                                </>
                              )
                            ) : (
                              <button
                                onClick={() => setReportComment(comment.id)}
                                className="flex items-center gap-1 transition-opacity opacity-60 hover:opacity-100"
                                style={{ color: 'var(--text-tertiary)' }}
                                aria-label="Report comment"
                              >
                                <Flag size={13} />
                                <span className="text-xs">Report</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Related Videos — mobile only (desktop uses sidebar) */}
          <div className="pt-4 md:hidden" style={{ borderTop: '1px solid var(--card-border)' }}>
            <h2 className="font-semibold mb-3 text-primary">More Like This</h2>
            <div className="space-y-3">
              {videos.filter(v => String(v.id) !== videoId).slice(0, 6).map(relatedVideo => (
                <div
                  key={relatedVideo.id}
                  onClick={() => {
                    setPlaying(false);
                    cleanupRef.current?.(); cleanupRef.current = null;
                    navigate(`/video/${relatedVideo.id}`);
                  }}
                  className="flex gap-3 cursor-pointer p-2 rounded-xl transition-all duration-300 hover-surface hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-black">
                    <img src={relatedVideo.thumbnail} alt={relatedVideo.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-sm mb-1 line-clamp-2 text-primary">{relatedVideo.title}</h3>
                    <p className="text-xs text-tertiary">{relatedVideo.creator}</p>
                    <p className="text-xs text-tertiary">{relatedVideo.views != null ? `${relatedVideo.views} views · ` : ''}{relatedVideo.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>{/* end details */}
        </div>{/* end left column */}

        {/* ═══ RIGHT: sidebar (desktop only) ═══ */}
        <aside className="hidden md:flex flex-col gap-4 self-start sticky top-[4rem]">
          <div className="rounded-2xl overflow-hidden" style={cardStyle}>
            <div className="px-4 pt-4 pb-3">
              <h2 className="font-semibold text-base text-primary">More Like This</h2>
            </div>
            <div className="px-4 pb-4 space-y-3">
              {videos.filter(v => String(v.id) !== videoId).slice(0, 8).map(relatedVideo => (
                <div
                  key={relatedVideo.id}
                  onClick={() => {
                    setPlaying(false);
                    cleanupRef.current?.(); cleanupRef.current = null;
                    navigate(`/video/${relatedVideo.id}`);
                  }}
                  className="flex gap-3 cursor-pointer group"
                >
                  <div className="w-24 h-[54px] rounded-lg overflow-hidden flex-shrink-0 bg-black">
                    <img src={relatedVideo.thumbnail} alt={relatedVideo.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-semibold line-clamp-2 leading-snug text-primary group-hover:text-brand-accent transition-colors">{relatedVideo.title}</h3>
                    <p className="text-xs text-tertiary mt-0.5 truncate">{relatedVideo.creator}</p>
                    <p className="text-xs text-tertiary">{relatedVideo.views != null ? `${relatedVideo.views} views · ` : ''}{relatedVideo.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

      </div>{/* end grid */}

      {reportComment && (
        <ReportModal
          contentType="video_comment"
          contentId={reportComment}
          onClose={() => setReportComment(null)}
        />
      )}
      {showReport && (
        <ReportModal contentType="video" contentId={video.id} onClose={() => setShowReport(false)} />
      )}
      {confirmRemove && (
        <RemoveVideoDialog
          isUpload={Boolean(video.streamHlsUrl)}
          onConfirm={handleRemoveVideo}
          onClose={() => setConfirmRemove(false)}
        />
      )}
      {showVerifyModal && (
        <VerifyModal onClose={() => setShowVerifyModal(false)} />
      )}

      <Toast message={toastMsg} />
    </div>
  );
};

export default VideoScreen;
